import React, { useState, useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import { styled, Box, Typography } from "@mui/material";
import axios from "axios";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { GET_GSC_ANALYTICS_DASHBOARD } from "../../gql/queries/analytics";

const COLORS = ["#4285f4", "#34a853", "#fbbc04", "#ea4335", "#9aa0a6"];

const AnalyticsDashboardWrapper = styled("section")(({ theme }) => ({
  marginTop: "20px",

  "& .dashboard-header": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    padding: "20px",
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#fff",
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,

    "@media (max-width:768px)": {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "15px",
    },
  },

  "& .dashboard-header h2": {
    margin: 0,
    fontSize: "1.8rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
  },

  "& .date-range-selector": {
    display: "flex",
    alignItems: "center",
    gap: "10px",

    "& label": {
      fontWeight: 500,
      color: theme.palette.text.primary,
    },

    "& select": {
      padding: "8px 12px",
      borderRadius: "6px",
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      fontSize: "0.95rem",
      cursor: "pointer",
    },
  },

  "& .metrics-grid": {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "20px",
    marginBottom: "20px",

    "@media (max-width:768px)": {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },

  "& .metric-card": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#fff",
    padding: "20px",
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
    textAlign: "center",
  },

  "& .metric-label": {
    fontSize: "0.9rem",
    color: theme.palette.text.secondary,
    marginBottom: "10px",
    fontWeight: 500,
  },

  "& .metric-value": {
    fontSize: "2rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },

  "& .dashboard-card": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#fff",
    padding: "25px",
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
    // marginBottom: "15px",
    marginTop: "15px",
  },

  "& .dashboard-card h3": {
    margin: "0 0 20px 0",
    fontSize: "1.3rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.divider}`,
    paddingBottom: "10px",
  },

  "& .dashboard-grid": {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(500px, 1fr))",
    gap: "20px",
    marginBottom: "20px",

    "@media (max-width:768px)": {
      gridTemplateColumns: "1fr",
    },
  },

  "& .table-container": {
    overflowX: "auto",
  },

  "& .analytics-table": {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.9rem",
  },

  "& .analytics-table thead": {
    backgroundColor: theme.palette.mode === "dark" ? "#1e1e1e" : "#f8f9fa",
  },

  "& .analytics-table th": {
    padding: "12px",
    textAlign: "left",
    fontWeight: 600,
    color: theme.palette.text.primary,
    borderBottom: `2px solid ${theme.palette.divider}`,
  },

  "& .analytics-table td": {
    padding: "10px 12px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
  },

  "& .analytics-table tbody tr:hover": {
    backgroundColor: theme.palette.action.hover,
  },

  "& .page-url": {
    maxWidth: "300px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontFamily: "monospace",
    fontSize: "0.85rem",
  },

  "& .query-text": {
    fontWeight: 500,
    color: theme.palette.text.primary,
  },

  "& .loading": {
    textAlign: "center",
    padding: "40px",
    color: theme.palette.text.secondary,
    fontSize: "1.1rem",
  },

  "& .error-message": {
    backgroundColor: theme.palette.mode === "dark" ? "#4a1f24" : "#f8d7da",
    color: theme.palette.mode === "dark" ? "#ffb3b3" : "#721c24",
    padding: "15px",
    borderRadius: "6px",
    textAlign: "center",
  },
}));

function AnalyticsDashboard({ email, siteUrl }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState("90"); // days

  const [loadDashboard] = useLazyQuery(GET_GSC_ANALYTICS_DASHBOARD, {
    fetchPolicy: "network-only",
    onCompleted: result => {
      if (result?.analyticsDashboard?.success) {
        setData(result.analyticsDashboard.data);
        setError(null);
      } else {
        setError("Failed to fetch analytics data");
      }
      setLoading(false);
    },
    onError: err => {
      setError(err.message || "Failed to fetch analytics data");
      setLoading(false);
    },
  });

  // useEffect(() => {
  //   if (siteUrl) {
  //     fetchDashboardData();
  //   }
  // }, [siteUrl, dateRange]);

  // const fetchDashboardData = async () => {
  //   setLoading(true);
  //   setError(null);

  //   try {
  //     const endDate = new Date().toISOString().split("T")[0];
  //     const startDate = new Date(Date.now() - parseInt(dateRange, 10) * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

  //     const params = new URLSearchParams({
  //       site_url: siteUrl,
  //       start_date: startDate,
  //       end_date: endDate,
  //     });

  //     if (email) {
  //       params.append("email", email);
  //     }

  //     const response = await axios.get(`http://localhost:8000/api/analytics/dashboard?${params.toString()}`);

  //     if (response.data.success) {
  //       setData(response.data.data);
  //     } else {
  //       setError("Failed to fetch analytics data");
  //     }
  //   } catch (err) {
  //     setError(err.response?.data?.detail || err.message || "Failed to fetch analytics data");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  useEffect(() => {
    if (!siteUrl) return;

    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteUrl, dateRange]);

  const fetchDashboardData = () => {
    setLoading(true);
    setError(null);

    const endDate = new Date().toISOString().split("T")[0];
    const startDate = new Date(Date.now() - Number(dateRange) * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

    loadDashboard({
      variables: {
        site_url: siteUrl,
        start_date: startDate,
        end_date: endDate,
        email: email || null,
      },
    });
  };

  if (loading) {
    return (
      // <Box className="analytics-dashboard">
      <AnalyticsDashboardWrapper>
        <Box className="dashboard-card">
          <Box className="loading">Loading analytics...</Box>
        </Box>
      </AnalyticsDashboardWrapper>
      // </Box>
    );
  }

  if (error) {
    return (
      // <Box className="analytics-dashboard">
      <AnalyticsDashboardWrapper>
        <Box className="dashboard-card">
          <Box className="error-message">{error}</Box>
        </Box>
      </AnalyticsDashboardWrapper>
      // </Box>
    );
  }

  if (!data) {
    return null;
  }

  return (
    // <Box className="analytics-dashboard">
    <AnalyticsDashboardWrapper>
      <Box className="dashboard-header">
        <Typography variant="h2">Analytics Dashboard</Typography>
        <Box className="date-range-selector">
          <Typography component="label" htmlFor="date-range">
            Date Range:{" "}
          </Typography>
          <select value={dateRange} onChange={e => setDateRange(e.target.value)}>
            <option value="30">Last 30 days</option>
            <option value="60">Last 60 days</option>
            <option value="90">Last 90 days</option>
            <option value="180">Last 6 months</option>
          </select>
        </Box>
      </Box>

      {/* Overview Cards */}
      <Box className="metrics-grid">
        <Box className="metric-card">
          <Box className="metric-label">Total Clicks</Box>
          <Box className="metric-value">{data.overview?.clicks?.toLocaleString() || 0}</Box>
        </Box>
        <Box className="metric-card">
          <Box className="metric-label">Total Impressions</Box>
          <Box className="metric-value">{data.overview?.impressions?.toLocaleString() || 0}</Box>
        </Box>
        <Box className="metric-card">
          <Box className="metric-label">Average CTR</Box>
          <Box className="metric-value">{data.overview?.ctr || 0}%</Box>
        </Box>
        <Box className="metric-card">
          <Box className="metric-label">Average Position</Box>
          <Box className="metric-value">{data.overview?.position || 0}</Box>
        </Box>
      </Box>

      {/* Trends Chart */}
      <Box className="dashboard-card">
        <Typography variant="h3">Performance Trends</Typography>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data.trends || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={80} />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="clicks" stroke="#4285f4" name="Clicks" strokeWidth={2} />
            <Line yAxisId="left" type="monotone" dataKey="impressions" stroke="#34a853" name="Impressions" strokeWidth={2} />
            <Line yAxisId="right" type="monotone" dataKey="ctr" stroke="#fbbc04" name="CTR %" strokeWidth={2} />
            <Line yAxisId="right" type="monotone" dataKey="position" stroke="#ea4335" name="Position" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <Box className="dashboard-grid">
        {/* Top Pages */}
        <Box className="dashboard-card">
          <Typography variant="h3">Top Pages</Typography>
          <Box className="table-container">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Page</th>
                  <th>Clicks</th>
                  <th>Impressions</th>
                  <th>CTR</th>
                  <th>Position</th>
                </tr>
              </thead>
              <tbody>
                {(data.topPages || []).slice(0, 10).map((page, index) => (
                  <tr>
                    <td className="page-url">{page.page}</td>
                    <td>{page.clicks.toLocaleString()}</td>
                    <td>{page.impressions.toLocaleString()}</td>
                    <td>{page.ctr}%</td>
                    <td>{page.position}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>

        {/* Top Queries */}
        <Box className="dashboard-card">
          <Typography variant="h3">Top Queries</Typography>
          <Box className="table-container">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Query</th>
                  <th>Clicks</th>
                  <th>Impressions</th>
                  <th>CTR</th>
                  <th>Position</th>
                </tr>
              </thead>
              <tbody>
                {(data.topQueries || []).slice(0, 10).map((query, index) => (
                  <tr>
                    <td className="query-text">{query.query}</td>
                    <td>{query.clicks.toLocaleString()}</td>
                    <td>{query.impressions.toLocaleString()}</td>
                    <td>{query.ctr}%</td>
                    <td>{query.position}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      </Box>

      <Box className="dashboard-grid">
        {/* Device Breakdown */}
        <Box className="dashboard-card">
          <Typography variant="h3">Device Breakdown</Typography>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={data.devices || []} dataKey="clicks" nameKey="device" cx="50%" cy="50%" outerRadius={80} label>
                {(data.devices || []).map((entry, index) => (
                  <Cell fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        {/* Country Breakdown */}
        <Box className="dashboard-card">
          <Typography variant="h3">Top Countries</Typography>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.countries?.slice(0, 10) || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="country" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="clicks" fill="#4285f4" name="Clicks" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      {/* Search Appearance */}
      {data.searchAppearance && data.searchAppearance.length > 0 && (
        <Box className="dashboard-card">
          <Typography variant="h3">Search Appearance</Typography>
          <Box className="table-container">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Appearance Type</th>
                  <th>Clicks</th>
                  <th>Impressions</th>
                  <th>CTR</th>
                  <th>Position</th>
                </tr>
              </thead>
              <tbody>
                {data.searchAppearance.map((item, index) => (
                  <tr>
                    <td>{item.appearance}</td>
                    <td>{item.clicks.toLocaleString()}</td>
                    <td>{item.impressions.toLocaleString()}</td>
                    <td>{item.ctr}%</td>
                    <td>{item.position}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      )}
    </AnalyticsDashboardWrapper>
    // {/* </Box> */}
  );
}
// plain
export default AnalyticsDashboard;
